<script setup lang="ts">
import { computed } from 'vue';

import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { NightsSelector } from '@apps/main/new-desktop/modules/nightsSelector';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import { useEditTripStore } from '@apps/main/new-desktop/core/stores/editTrip/editTripStore';

import { AppSvgIcon, AppFlicker, AppTrashCanButton } from '@libs/ui';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import type {
  ItineraryDateLocationLoaderSegment,
  ItineraryDateLocationSegment,
} from '../../../models';
import { useTripOverviewModalsStore } from '../../../stores';

const { t } = i18n.global;
interface ItineraryDateLocationSegmentProps {
  segment: ItineraryDateLocationSegment | ItineraryDateLocationLoaderSegment;
  printMode: boolean;
}
const props = defineProps<ItineraryDateLocationSegmentProps>();

interface ItineraryDateLocationSegmentEmits {
  (event: 'segment-clicked'): void;
}
const emit = defineEmits<ItineraryDateLocationSegmentEmits>();

const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const editTripStore = useEditTripStore();

const isSegmentLoaded = computed<boolean>(() => 'time' in props.segment);

const nightsText = computed(
  () =>
    `${props.segment.numberOfNights ?? 0} ${t(
      'planner.nights',
      props.segment.numberOfNights ?? 0
    ).toLocaleLowerCase()}`
);

const countryText = computed(() =>
  props.segment.location.country ? `, ${props.segment.location.country}` : ''
);

const dateText = computed(() =>
  browserStore.isSmallerThan900px
    ? (props.segment as ItineraryDateLocationSegment).time.absoluteShortDays
    : (props.segment as ItineraryDateLocationSegment).time.absoluteDays
);

function onTrashCanClick(ingredientId: IngredientId): void {
  tripOverviewModalsStore.openRemoveDestinationModal(ingredientId);
}

function changeNumberOfNights(
  segment: ItineraryDateLocationSegment | ItineraryDateLocationLoaderSegment,
  newNumberOfNights: number
): void {
  editTripStore.changeNumberOfNights(segment.ingredientId, newNumberOfNights);
}
</script>

<template>
  <button
    class="itinerary-date-location-segment"
    :class="{ 'print-mode': props.printMode }"
    @click.stop="emit('segment-clicked')"
  >
    <div class="day-range">
      <span>{{ $t('planner.days', [0]) }}</span>
      <span v-if="isSegmentLoaded" class="days">
        {{ (segment as ItineraryDateLocationSegment).time.relativeDays }}
      </span>
      <AppFlicker v-else width="spacing-32" height="spacing-20" />
    </div>
    <div class="destination-information">
      <div>{{ segment.location.name }} {{ countryText }}</div>
      <div class="second-row">
        <span v-if="isSegmentLoaded">
          {{ dateText }}
        </span>
        <AppFlicker v-else width="spacing-96" height="spacing-16" />
        <div v-if="browserStore.isSmallerThan900px">
          ,&nbsp;

          <span>
            {{ nightsText }}
          </span>
        </div>
      </div>
    </div>
    <div v-if="!props.printMode">
      <AppSvgIcon
        v-if="browserStore.isSmallerThan900px"
        name="threeDotsHorizontal"
        size="18"
      />
      <div v-else class="destination-actions-desktop">
        <button
          class="nights-selector-desktop no-parent-hover"
          @click.stop="
            statsigEventsStore.logCoreNightsSelectorOpened({
              source: CoreEventSource.TripOverview,
            })
          "
        >
          <NightsSelector
            :number-of-nights="segment.numberOfNights"
            app-select-type="transparent"
            app-select-size="size-m"
            :flicker-margin="{ xSpacing: 'spacing-8' }"
            :disabled="props.printMode"
            font-variant="font-bold"
            @change-number-of-nights="changeNumberOfNights(segment, $event)"
          />
        </button>
        <AppTrashCanButton
          class="no-parent-hover"
          :aria-label="$t('a11y.remove_destination')"
          @click.stop="onTrashCanClick(segment.ingredientId)"
        />
      </div>
    </div>
  </button>
</template>

<style scoped lang="scss">
.itinerary-date-location-segment {
  all: unset;
  width: -webkit-fill-available;
  position: relative;
  display: grid;
  grid-template-columns: min-content 1fr min-content;
  padding: var(--spacing-16) var(--spacing-12) var(--spacing-16)
    var(--spacing-8);
  gap: var(--spacing-8);
  border-radius: var(--border-radius-8);
  border: 1px solid var(--color-neutral-40);
  background-color: var(--color-white);
  color: var(--color-neutral-70);
  margin: 0 var(--spacing-24) var(--spacing-24) var(--spacing-24);
  align-items: center;

  @include for-less-or-equal-than-900-px {
    padding: var(--spacing-12) var(--spacing-12) var(--spacing-12)
      var(--spacing-8);
  }
  @include for-less-or-equal-than-599-px {
    margin: 0 var(--spacing-12) var(--spacing-24) var(--spacing-12);
  }

  &:not(.print-mode) {
    @include on-hover() {
      /* stylelint-disable-next-line selector-pseudo-class-disallowed-list */
      &:not(:has(.no-parent-hover:hover)) {
        background: var(--color-neutral-20);
        cursor: pointer;
      }
    }
  }

  .day-range {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 40px;
    @include font-body-m-light;
    @include for-less-or-equal-than-900-px {
      @include font-body-s-light;
    }
    .days {
      @include font-body-m-bold;
      @include for-less-or-equal-than-900-px {
        @include font-body-s-bold;
      }
    }
  }

  .destination-information {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);
    @include font-label-l-bold;
    @include for-less-or-equal-than-900-px {
      gap: var(--spacing-2);
      @include font-body-m-bold;
    }

    .second-row {
      @include font-label-l-light;
      @include for-less-or-equal-than-900-px {
        display: flex;
        flex-direction: row;
        @include font-body-m-light;
      }
    }
  }

  &::after {
    content: '';
    height: 25px;
    width: 0px;
    border-right: 1px dashed var(--color-neutral-70);
    position: absolute;
    bottom: -25px;
    left: 24px;
  }

  .destination-actions-desktop {
    display: flex;
    align-items: center;
    .nights-selector-desktop {
      all: unset;
      height: 100%;
      display: flex;
      align-items: center;
      border-right: 1px solid var(--color-neutral-20);
      margin-right: var(--spacing-8);
    }
  }
}
</style>
